import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from '@material-ui/core';
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from 'prop-types';
import AmountAutoComplete from 'src/components/PurchaseOrderDetails/inputs/AmountAutoComplete';
import { formatCurrency } from 'src/utils/currency';

const useStyles = makeStyles((theme) => ({
  dialogTitleConatiner: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
  },
  dialogTitleText: {
    color: theme.palette.getContrastText(theme.palette.primary.dark),
    fontSize: theme.typography.h3.fontSize,
  },
  datePickerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'right',
  },
  valueField: {
    width: '150px',
    textAlign: 'right',
  },
}));

function ChargeInfoDialog({
  availableCharges,
  appliedCharges,
  isDialogOpen,
  setIsDialogOpen,
  handleApply,
  selectedCharge,
  setSelectedCharge,
  purchaseOrderDetails,
  isChargeReadOnly,
  providerCategory,
}) {
  const classes = useStyles();

  const [updatedFields, setUpdatedFields] = useState();
  const [chargeCategory, setChargeCategory] = useState();


  useEffect(() => handleSelectionChange(selectedCharge), [selectedCharge]);

  const changeChargeCategory = (selectedCatergoryId) => {
    if (chargeCategory !== selectedCatergoryId){
      setSelectedCharge(null);
    }
    setChargeCategory(selectedCatergoryId);
  };

  const handleClose = (reason) => {
    if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
      return false;
    }
  };

  function handleSetFocus(e) {
    e.target.setSelectionRange(0, 0);
  }

  function handleKeyDown(e, updatedFields) {
    if (e.key === 'Enter') {
      e.stopPropagation();

      handleApply(updatedFields);
      setTimeout(() => {
        setSelectedCharge(null);
        setIsDialogOpen(false);
      }, 50);
    }
  }
  const handleAmountChange = (name, value) => {
    value = value.replace(/[^0-9.]/g, '');
    const mutatedUpdatedFields = [...updatedFields];
    const modifiedFields = mutatedUpdatedFields.filter((o) => o.name == name);

    if (providerCategory != 'Out Of Network') {
      if (selectedCharge.purchaseOrderChargeTypeName == 'UnloadedMileageAmount') {
        modifiedFields[0].amount = value;
        const mileage = mutatedUpdatedFields[0];
        const free = mutatedUpdatedFields[1];
        const rate = mutatedUpdatedFields[2];
        const amount = mutatedUpdatedFields[3];
        const total = (Number(mileage.amount) - Number(free.amount)) * Number(rate.amount);

        amount.amount = formatCurrency(total).substring(1);
        setUpdatedFields(mutatedUpdatedFields);
        return;
      }

      if (selectedCharge.purchaseOrderChargeTypeName == 'LoadedMileageAmount') {
        modifiedFields[0].amount = value;

        const mileage = mutatedUpdatedFields[0];
        const free = mutatedUpdatedFields[1];
        const rate = mutatedUpdatedFields[2];
        const amount = mutatedUpdatedFields[3];
        const total = (Number(mileage.amount) - Number(free.amount)) * Number(rate.amount);

        amount.amount = formatCurrency(total).substring(1);
        setUpdatedFields(mutatedUpdatedFields);
        return;
      }

      if (selectedCharge.purchaseOrderChargeTypeName == 'StorageAmount') {
        modifiedFields[0].amount = value;

        const daysInStorage = mutatedUpdatedFields[0];
        const storageRate = mutatedUpdatedFields[1];
        const total = Number(daysInStorage.amount) * Number(storageRate.amount);
        const amount = mutatedUpdatedFields[2];
        amount.amount = formatCurrency(total).substring(1);
        setUpdatedFields(mutatedUpdatedFields);
        return;
      }
    }

    if (modifiedFields.length > 0)
      modifiedFields[0].amount = value;

    setUpdatedFields(mutatedUpdatedFields);
  };

  function handleSelectionChange(charge) {
    if (!selectedCharge || !selectedCharge.purchaseOrderChargeTypeId) {
      setUpdatedFields(null);
      return null;
    }

    const newUpdatedFields = [{ ...charge }];
    const existingAppliedAmounts = appliedCharges.filter(o => o.purchaseOrderChargeTypeId == charge.purchaseOrderChargeTypeId);
    if (existingAppliedAmounts && existingAppliedAmounts.length > 0) {
      newUpdatedFields[0].amount = formatCurrency(existingAppliedAmounts[0].amount).substring(1);
    } else {
      newUpdatedFields[0].amount = '0.00';
    }

    if (charge.purchaseOrderChargeTypeName == 'UnloadedMileageAmount') {
      const unloadedMileageRate = purchaseOrderDetails.UnloadedMileageRate;
      const freeUnloadedMileage = purchaseOrderDetails.FreeUnloadedMiles;
      const unloadedMileage = purchaseOrderDetails.MilesUnloaded;
      newUpdatedFields.unshift({ name: 'UnloadedMileageRate', formattedName: 'Unloaded Mileage Rate', amount: unloadedMileageRate, isCalculated: true });
      newUpdatedFields.unshift({ name: 'UnloadedFreeMiles', formattedName: 'Free Unloaded Mileage', amount: freeUnloadedMileage, isCalculated: true });
      newUpdatedFields.unshift({ name: 'UnloadedMileage', formattedName: 'Total Unloaded Mileage', amount: unloadedMileage });
    }

    if (charge.purchaseOrderChargeTypeName == 'LoadedMileageAmount') {
      const loadedMileageRate = purchaseOrderDetails.LoadedMileageRate;
      const freeLoadedMileage = purchaseOrderDetails.FreeLoadedMiles;
      const loadedMileage = purchaseOrderDetails.MilesLoaded;
      newUpdatedFields.unshift({ name: 'LoadedMileageRate', formattedName: 'Loaded Mileage Rate', amount: loadedMileageRate, isCalculated: true });
      newUpdatedFields.unshift({ name: 'LoadedFreeMiles', formattedName: 'Free Loaded Mileage', amount: freeLoadedMileage, isCalculated: true });
      newUpdatedFields.unshift({ name: 'LoadedMileage', formattedName: 'Total Loaded Mileage', amount: loadedMileage });
    }

    if (charge.purchaseOrderChargeTypeName == 'StorageAmount') {
      if (charge.categoryId == "2") {
        const secondaryStorageDailyRate = purchaseOrderDetails.SecondaryStorageDailyRate;
        const secondaryStorageDays = purchaseOrderDetails.SecondaryStorageDays;

        newUpdatedFields.unshift({ name: 'SecondaryDailyStorageRate', formattedName: 'Secondary Daily Storage Rate', amount: secondaryStorageDailyRate });
        newUpdatedFields.unshift({ name: 'SecondaryDaysInStorage', formattedName: 'Secondary Days In Storage', amount: secondaryStorageDays });
      }
      else {
        const storageDailyRate = purchaseOrderDetails.StorageDailyRate;
        const storageDays = purchaseOrderDetails.StorageDays;

        newUpdatedFields.unshift({ name: 'DailyStorageRate', formattedName: 'Daily Storage Rate', amount: storageDailyRate });
        newUpdatedFields.unshift({ name: 'DaysInStorage', formattedName: 'Days In Storage', amount: storageDays });
      } 
    }

    changeChargeCategory(parseInt(charge.categoryId) ?? 1);
    setSelectedCharge(charge);
    setUpdatedFields(newUpdatedFields);
  }

  const filteredCharges = availableCharges?.filter(
    (charge) => charge.categoryId === chargeCategory?.toString()
  ) || [];

  return (
    <>
      <Dialog
        open={isDialogOpen}
        onClose={(_, reason) => handleClose(reason)}
        aria-labelledby="customDateRangePickerTitle"
        aria-describedby="customDateRangePickerContent"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          className={classes.dialogTitleConatiner}
          id="customDateRangePickerTitle"
          data-test="customDateRangePickerTitle"
        >
          <Typography
            className={classes.dialogTitleText}
          >
            Update Purchase Order Charges
          </Typography>
        </DialogTitle>
        <DialogContent >
        <Box display="flex" justifyContent="center" mb={2}>
          <FormControl>
          <FormLabel id="category-row-radio-buttons-group-label">Select A Charge Category</FormLabel>
          <RadioGroup
            row
            aria-labelledby="category-row-radio-buttons-group-label"
            name="row-radio-buttons-group"
          >
            <FormControlLabel 
              disabled={!isChargeReadOnly} 
              value="Provider" 
              control={<Radio />} 
              label="Provider"
              checked={chargeCategory === 1}
              onChange={() => changeChargeCategory(1)}
            />
            <FormControlLabel 
              disabled={!isChargeReadOnly} 
              value="Secondary" 
              control={<Radio />} 
              label="Secondary"
              checked={chargeCategory === 2}
              onChange={() => changeChargeCategory(2)}
            />
          </RadioGroup>
        </FormControl>
        </Box>
          <Box className={classes.datePickerContainer}>
          {chargeCategory && (
            <AmountAutoComplete
              disabled={!isChargeReadOnly}
              availableCharges={filteredCharges}
              setSelectedCharge={setSelectedCharge}
              selectedCharge={selectedCharge}
            /> )}
          </Box>
          {updatedFields &&
            <Table>
              <TableBody>
                {updatedFields.map(o => (
                  <TableRow key={o.purchaseOrderChargeTypeId}>
                    <TableCell className={classes.fontWeightMedium}>{o.formattedName}</TableCell>
                    <TableCell align="right">
                      <TextField
                        className={classes.valueField}
                        variant="outlined"
                        autoFocus
                        onFocus={handleSetFocus}
                        value={o.amount}
                        disabled={o.isCalculated && providerCategory !== 'Out Of Network'}
                        onKeyDown={e => handleKeyDown(e, updatedFields)}
                        onChange={(event) => handleAmountChange(o.name, event.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>}
        </DialogContent>
        <DialogActions
          className={classes.actionsContainer}
        >
          <Button
            id="closeDialogButton"
            color="primary"
            variant="contained"
            onClick={() => setIsDialogOpen(false)}
          >
            Close
          </Button>
          <Button
            id="applyDialogButton"
            color="primary"
            disabled={!selectedCharge?.purchaseOrderChargeTypeId}
            variant="contained"
            onClick={() => handleApply(updatedFields)}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

ChargeInfoDialog.propTypes = {
  availableCharges: PropTypes.arrayOf(PropTypes.shape({
    purchaseOrderChargeTypeId: PropTypes.Number,
    purchaseOrderChargeTypeName: PropTypes.string,
    isCalculated: PropTypes.bool,
    isExternalEditable: PropTypes.bool,
    isInternalEditable: PropTypes.bool,
    categoryId: PropTypes.number,
  })),
  appliedCharges: PropTypes.arrayOf(PropTypes.shape({
    purchaseOrderChargeId: PropTypes.string,
    poNumber: PropTypes.number,
    purchaseOrderChargeTypeId: PropTypes.string,
    purchaseOrderChargeTypeName: PropTypes.string,
    amount: PropTypes.number,
    isCalculated: PropTypes.bool,
    isExternalEditable: PropTypes.bool,
    isInternalEditable: PropTypes.bool,
    categoryId: PropTypes.number,
  })),
  //appliedCharges: PropTypes.arrayOf,
  isDialogOpen: PropTypes.bool,
  setIsDialogOpen: PropTypes.func,
  handleCustomDateRangeSave: PropTypes.func,
  purchaseOrderDetails: PropTypes.shape({
    AmountOwed: PropTypes.number,
    LoadedMileageRate: PropTypes.number,
    MilesLoaded: PropTypes.number,
    FreeLoadedMiles: PropTypes.number,
    UnloadedMileageRate: PropTypes.number,
    MilesUnloaded: PropTypes.number,
    FreeUnloadedMiles: PropTypes.number,
    StorageDailyRate: PropTypes.number,
    StorageDays: PropTypes.number,
    SecondaryStorageDailyRate: PropTypes.number,
    SecondaryStorageDays: PropTypes.number,
  }),
  selectedCharge: PropTypes.shape({
    purchaseOrderChargeTypeId: PropTypes.number,
    purchaseOrderChargeTypeName: PropTypes.string,
    isCalculated: PropTypes.bool,
    isExternalEditable: PropTypes.bool,
    isInternalEditable: PropTypes.bool,
    categoryId: PropTypes.number,
  }),
  setSelectedCharge: PropTypes.func,
  handleApply: PropTypes.func,
  isChargeReadOnly: PropTypes.bool,
  providerCategory: PropTypes.string,
};


export default ChargeInfoDialog;
